// Ce que l'app sait du compte connecté, chargé une fois après la connexion.
import { signOut } from '../lib/auth';
import { adminClubs, accountStillExists, currentUserId, isPlatformAdmin, isTeamStaff, myClubRoles, myClubs, myCoachTeamIds, myMemberships, myPartners, myPeople, myProfileName, myTeams, partnerReport, teamSquads, type Club, type ClubBrand, type Partner, type Person, type Squad, type Team } from '../lib/api';
import { agree, displayName } from '../domain/labels';
import type { TeamGender } from '../domain/labels';
import { storedBrand } from './theme';
import { followerClubs } from '../lib/follow-api';
import { clubModules, type ModulesState } from '../lib/modules-api';
import { siteOffered } from '../lib/site-api';
import { BETA_MODULES, DEFAULT_ON, type ModuleKey } from '../domain/modules';
import { freeMyClubs } from '../lib/gratuite-api';
import type { FreeClub } from '../domain/gratuite';

export interface Ctx {
  uid: string;
  people: Person[];                                   // moi + mes enfants
  teams: Team[];                                      // équipes visibles (staff ou membre)
  staffOf: Set<string>;                               // équipes que j'encadre (ou admin) : les DROITS
  coachOf: Set<string>;                               // équipes où je suis vraiment coach : le RÔLE affiché
  profileName: string | null;                         // prénom du compte (parents, coachs, bureau)
  partners: Partner[];                                // partenaires dont je suis un compte (D-037)
  memberships: { team_id: string; person_id: string; squad_ids?: string[] }[];
  squads: Squad[];                                    // équipes engagées de mes groupes (SF1, SF2…, D-042)
  adminOf: Club[];                                    // clubs dont je suis admin
  clubs: ClubBrand[];                                 // clubs dont je suis membre, avec leurs couleurs
  roles: { club_id: string; role: string }[];         // mes rôles au club (dont « supporter », D-064)
  club: ClubBrand | null;                             // le club affiché (couleurs, logo, nom)
  hats: Hat[];                                        // mes casquettes (D-072) : des onglets en haut si j'en ai plusieurs
  hat: HatKey | null;                                 // la casquette affichée
  full: FullView;                                     // tout ce que je vois, toutes casquettes confondues
  platformAdmin: boolean;                             // l'éditeur (IDR) : la console, tous les clubs (D-081)
  followerOf: string[];                               // clubs dont je suis seulement abonné (D-110) : leurs matchs, mes suivis
  modules: Map<string, ModulesState | null>;          // les modules activés de chacun de mes clubs (D-122)
  siteOffer?: Set<string>;                            // D-142 : mes clubs à qui l'offre « Site personnalisable » est proposée
  free?: Map<string, FreeClub>;                       // D-166 : mes clubs en mode « équipe gratuite » (état, demande au club)
}

/** Une casquette (D-072) : une vue de l'app pour un rôle — bureau, responsable technique, coach,
 *  joueuse ou joueur, parent, partenaire, et Bénévole (D-097) pour tout compte adulte du club.
 *  Les champs visibles du contexte (équipes, personnes, droits affichés) sont ceux de la casquette
 *  choisie ; `full` garde tout. Les droits réels restent en base. */
export type HatKey = 'console' | 'bureau' | 'technique' | 'coach' | 'joueur' | 'parent' | 'partenaire' | 'benevole';
export interface Hat { key: HatKey; icon: string; label: string }
export interface FullView {
  people: Person[]; teams: Team[]; memberships: Ctx['memberships']; adminOf: Club[]; staffOf: Set<string>; coachOf: Set<string>;
  pureCoach: Set<string>;                            // équipes où je suis coach (team_staff)
  techOf: Set<string>;                               // équipes de mes clubs de responsable technique
}

export async function loadContext(): Promise<Ctx | null> {
  const uid = await currentUserId();
  if (!uid) return null;
  // Session d'un compte qui n'existe plus (démo remise à zéro, compte supprimé) : retour à la connexion.
  if (!(await accountStillExists())) { await signOut().catch(() => undefined); return null; }
  const [people, teams, adminOf, clubs, coachIds, profileName, partners, roles, platformAdmin] = await Promise.all([
    myPeople(), myTeams(), adminClubs(), myClubs(), myCoachTeamIds(uid).catch(() => [] as string[]), myProfileName().catch(() => null),
    myPartners().catch(() => [] as Partner[]), myClubRoles().catch(() => [] as { club_id: string; role: string }[]),
    isPlatformAdmin().catch(() => false),
  ]);
  // Compte partenaire seul : pas membre du club ; ses couleurs viennent de son espace (D-037).
  if (!clubs.length && partners.length) {
    const r = await partnerReport(partners[0]!.report_token).catch(() => null);
    if (r) clubs.push({ id: partners[0]!.club_id, name: r.club.name, short_name: r.club.short_name, logo_url: r.club.logo, primary_color: r.club.color });
  }
  // Abonné (D-110) : un compte créé depuis la page publique, qui n'est pas membre du club ; ses couleurs.
  let followerOf: string[] = [];
  if (!clubs.length && !partners.length) {
    const fc = await followerClubs().catch(() => []);
    for (const f of fc) clubs.push({ id: f.id, name: f.name, short_name: f.short_name, logo_url: f.logo_url, primary_color: f.primary_color, secondary_color: f.secondary_color });
    followerOf = fc.map(f => f.id);
  }
  const [memberships, staffFlags, squads, modStates, offers, frees] = await Promise.all([
    myMemberships(people.map(p => p.id)),
    Promise.all(teams.map(t => isTeamStaff(t.id))),
    teamSquads(teams.map(t => t.id)).catch(() => [] as Squad[]),
    Promise.all(clubs.map(c => clubModules(c.id).catch(() => null))),   // D-122 : les modules de chaque club
    Promise.all(clubs.map(c => (followerOf.includes(c.id) ? false : siteOffered(c.id).catch(() => false)))),   // D-142 : l'offre du site
    followerOf.length ? Promise.resolve([] as FreeClub[]) : freeMyClubs().catch(() => [] as FreeClub[]),   // D-166 : l'équipe gratuite
  ]);
  const modules = new Map(clubs.map((c, i) => [c.id, modStates[i] ?? null] as const));
  const siteOffer = new Set(clubs.filter((_, i) => offers[i] === true).map(c => c.id));
  const free = new Map((frees ?? []).map(f => [f.club_id, f] as const));
  // Club affiché : celui choisi la dernière fois sur cet appareil, sinon celui de
  // mes équipes, sinon le premier. (Plusieurs clubs : un parent, un coach…)
  const last = storedBrand()?.id;
  const club = clubs.find(c => c.id === last) ?? clubs.find(c => teams.some(t => t.club_id === c.id)) ?? clubs[0] ?? null;
  const staffOf = new Set(teams.filter((_, i) => staffFlags[i]).map(t => t.id));
  // Responsable technique (D-068) : coach de toutes les équipes de son club.
  const techClubs = new Set(roles.filter(r => r.role === 'technical').map(r => r.club_id));
  const pureCoach = new Set(coachIds.filter(id => teams.some(t => t.id === id)));
  const techOf = new Set(teams.filter(t => techClubs.has(t.club_id) && staffOf.has(t.id)).map(t => t.id));
  const coachOf = new Set([...pureCoach, ...techOf]);
  const full: FullView = { people, teams, memberships, adminOf, staffOf, coachOf, pureCoach, techOf };
  const ctx: Ctx = { uid, people, teams, memberships, squads, adminOf, clubs, club, profileName, partners, roles, staffOf, coachOf, full, hats: [], hat: null, platformAdmin, followerOf, modules, siteOffer, free };
  ctx.hats = hatsOf(ctx);
  if (ctx.hats.length > 1) {
    // La dernière casquette choisie sur cet appareil, sinon la casquette principale (jamais Bénévole, D-097).
    const stored = storedHat(uid);
    applyHat(ctx, ctx.hats.some(h => h.key === stored) ? stored! : mainHat(ctx)!);
  }
  return ctx;
}

// --- Casquettes (D-072) ---------------------------------------------------------------
const HAT_KEY = (uid: string) => `arceau.casquette.${uid}`;
const storedHat = (uid: string): HatKey | null => { try { return localStorage.getItem(HAT_KEY(uid)) as HatKey | null; } catch { return null; } };

/** Mes casquettes, dans l'ordre des onglets : les rôles d'abord, Bénévole en dernier (D-097). */
function hatsOf(c: Ctx): Hat[] {
  const f = c.full, hats: Hat[] = [];
  const self = f.people.find(p => p.user_id === c.uid);
  const selfTeams = self ? f.teams.filter(t => f.memberships.some(m => m.person_id === self.id && m.team_id === t.id)) : [];
  const kids = f.people.filter(p => p.user_id !== c.uid);
  const coachTeams = f.teams.filter(t => f.pureCoach.has(t.id));
  const more = (n: number) => (n > 1 ? ` +${n - 1}` : '');
  if (c.platformAdmin) hats.push({ key: 'console', icon: '🛠', label: 'Console' });
  if (f.adminOf.length) hats.push({ key: 'bureau', icon: '🏛', label: 'Bureau' });
  if (f.techOf.size) hats.push({ key: 'technique', icon: '📋', label: 'Resp. technique' });
  if (coachTeams.length) hats.push({ key: 'coach', icon: '🎽', label: `Coach · ${coachTeams[0]!.name}${more(coachTeams.length)}` });
  if (self && selfTeams.length) hats.push({ key: 'joueur', icon: '🏀', label: `${agree(selfTeams[0]!.gender, 'Joueur', 'Joueuse')} · ${selfTeams[0]!.name}${more(selfTeams.length)}` });
  if (kids.length) hats.push({ key: 'parent', icon: '👪', label: `Parent · ${displayName(kids[0]!)}${more(kids.length)}` });
  // Compte partenaire (D-037) : son espace est une casquette quand il est aussi bénévole (D-071).
  if (partnerOnly(c)) hats.push({ key: 'partenaire', icon: '🤝', label: 'Partenaire' });
  if (isVolunteer(c)) hats.push({ key: 'benevole', icon: '❤️', label: 'Bénévole' });
  return hats;
}

/** Un module est-il activé pour le club affiché (D-122) ? État inconnu (pas encore lu, panne) : on le montre — rien ne
 *  disparaît sur une erreur de réseau. */
export function modOn(c: Pick<Ctx, 'club' | 'modules'>, key: ModuleKey, clubId = c.club?.id): boolean {
  const st = clubId ? c.modules?.get(clubId) : undefined;
  if (!st) return !BETA_MODULES.includes(key);   // une bêta n'apparaît jamais sur une panne
  // Sans ligne, un module est éteint ; sauf ceux allumés par défaut (D-149 : les skins des joueuses), comme module_on.
  const row = st.modules.find(m => m.module === key);
  return row ? row.enabled : DEFAULT_ON.includes(key);
}
/** D-142 : l'offre « Site personnalisable » est-elle proposée au club affiché ? Seulement les clubs de la bêta (le club pilote,
 *  la démo) tant que le lot B n'est pas livré ; un club qui a déjà un site ; tous avec le réglage de la plateforme
 *  `site_offer` = « tous » (supabase/migrations/20261104410000_site_beta.sql). Inconnu : non (l'offre ne s'annonce pas). */
export const siteOn = (c: Pick<Ctx, 'club' | 'siteOffer'>, clubId = c.club?.id): boolean => !!clubId && !!c.siteOffer?.has(clubId);
/** Un module en bêta (D-141) est-il ouvert au club affiché ? Les autres modules : toujours. État inconnu : non (une bêta
 *  n'apparaît jamais par erreur). */
export function betaOpen(c: Pick<Ctx, 'club' | 'modules'>, key: ModuleKey, clubId = c.club?.id): boolean {
  if (!BETA_MODULES.includes(key)) return true;
  const st = clubId ? c.modules?.get(clubId) : undefined;
  return !!st?.betas?.includes(key);
}
/** « Mains vives », le premier jeu caméra (D-164), est-il ouvert à ce club (la bêta : clubs pilotes, démo) ? Inconnu : non. */
export const mvOpen = (c: Pick<Ctx, 'club' | 'modules'>, clubId = c.club?.id): boolean => !!clubId && !!c.modules?.get(clubId)?.betas?.includes('mainsvives');
/** L'état des modules du club affiché (Club › Modules), ou null. */
export const modState = (c: Pick<Ctx, 'club' | 'modules'>): ModulesState | null => (c.club ? c.modules?.get(c.club.id) ?? null : null);

/** Relit les modules du club affiché (après une activation) ; les casquettes suivent (la casquette Bénévole). */
export async function refreshModules(c: Ctx): Promise<void> {
  if (!c.club) return;
  const st = await clubModules(c.club.id).catch(() => null);
  c.modules.set(c.club.id, st);
  refreshHats(c);
}

/** Les casquettes du club affiché (un autre club, un module activé ou éteint) : la casquette affichée reste si elle existe. */
export function refreshHats(c: Ctx): void {
  const before = c.hat;
  c.hats = hatsOf(c);
  if (c.hats.length > 1) applyHat(c, c.hats.some(h => h.key === before) ? before : mainHat(c));
  else applyHat(c, null);
}

/** Compte partenaire seul (D-037) : ni joueur, ni parent, ni bureau, ni staff. */
const partnerOnly = (c: Ctx) => c.partners.length > 0 && !c.full.people.length && !c.full.adminOf.length && !c.full.coachOf.size;

/** La casquette Bénévole (D-097) : tout compte adulte du club (D-064) ; un compte partenaire seulement s'il
 *  est aussi supporter (D-071) ; pas les mineurs pour l'instant (leur bénévolat reste sur leur accueil, D-041) ;
 *  jamais quand le club n'a pas activé le bénévolat (D-122). */
function isVolunteer(c: Ctx): boolean {
  if (!modOn(c, 'benevolat')) return false;
  const f = c.full;
  const member = partnerOnly(c) ? c.roles.some(r => r.role === 'supporter') : c.roles.length > 0 || f.adminOf.length > 0 || f.people.length > 0 || f.coachOf.size > 0;
  return member && !isMinorAccount(c);
}

/** La casquette principale : la console pour l'éditeur, puis celle du rôle principal (le responsable
 *  technique qui entraîne aussi : la sienne, D-093) ; Bénévole seulement s'il n'y a qu'elle (D-097). */
export function mainHat(c: Ctx): HatKey | null {
  const f = c.full;
  const p = personaOf({ ...c, people: f.people, teams: f.teams, memberships: f.memberships, adminOf: f.adminOf, staffOf: f.staffOf, coachOf: f.coachOf, hat: null });
  const want: HatKey | null = c.platformAdmin ? 'console' : p === 'coach' ? (f.techOf.size ? 'technique' : 'coach')
    : p === 'bureau' || p === 'parent' || p === 'joueur' || p === 'partenaire' ? p : null;
  return want && c.hats.some(h => h.key === want) ? want : c.hats[0]?.key ?? null;
}

/** La casquette Bénévole est à part et n'est pas celle affichée : l'accueil du rôle ne garde que le rôle,
 *  et une ligne « Le club a besoin de toi » mène à la casquette Bénévole (D-097). */
export const volunteerApart = (c: Ctx) => c.hats.length > 1 && c.hat !== 'benevole' && c.hats.some(h => h.key === 'benevole');

/** Affiche une casquette : les champs visibles du contexte deviennent ceux de ce rôle. */
export function applyHat(c: Ctx, key: HatKey | null): void {
  const f = c.full;
  c.hat = key;
  const self = f.people.filter(p => p.user_id === c.uid);
  const view = (people: Person[], teams: Team[], extra: Partial<Ctx> = {}) => {
    const ids = new Set(people.map(p => p.id));
    Object.assign(c, { people, teams, memberships: f.memberships.filter(m => ids.has(m.person_id) && teams.some(t => t.id === m.team_id)),
      adminOf: [], staffOf: new Set<string>(), coachOf: new Set<string>(), ...extra });
  };
  if (key === 'console' || key === 'partenaire') { view([], [], { memberships: [] }); return; }
  if (!key || c.hats.length < 2) { Object.assign(c, { people: f.people, teams: f.teams, memberships: f.memberships, adminOf: f.adminOf, staffOf: f.staffOf, coachOf: f.coachOf }); return; }
  // Bénévole (D-097) : le supporter qu'est tout le monde ; ni équipe, ni enfant, ni droit du bureau.
  if (key === 'benevole') view(self, [], { memberships: [] });
  else if (key === 'bureau') view(self, f.teams, { adminOf: f.adminOf, staffOf: f.staffOf, memberships: [] });
  else if (key === 'technique') view(self, f.teams.filter(t => f.techOf.has(t.id)), { staffOf: f.techOf, coachOf: f.techOf, memberships: [] });
  else if (key === 'coach') view(self, f.teams.filter(t => f.pureCoach.has(t.id)), { staffOf: f.pureCoach, coachOf: f.pureCoach, memberships: [] });
  else if (key === 'joueur') {
    const mine = new Set(f.memberships.filter(m => self.some(p => p.id === m.person_id)).map(m => m.team_id));
    view(self, f.teams.filter(t => mine.has(t.id)));
  } else {
    const kids = f.people.filter(p => p.user_id !== c.uid);
    const theirs = new Set(f.memberships.filter(m => kids.some(k => k.id === m.person_id)).map(m => m.team_id));
    view(kids, f.teams.filter(t => theirs.has(t.id)));
  }
}

/** Change de casquette (et s'en souvient sur cet appareil). `chosen` : la personne l'a choisie (barre des
 *  casquettes, « Bénévole › », visite guidée) ; sinon c'est une page d'un autre rôle qui l'a changée, et
 *  l'app l'annonce d'un petit bandeau (D-101). */
export function switchHat(c: Ctx, key: HatKey, chosen = false): void {
  try { localStorage.setItem(HAT_KEY(c.uid), key); } catch { /* navigation privée */ }
  if (chosen) hatChosen = true;
  applyHat(c, key);
}
let hatChosen = false;
/** La dernière casquette changée l'a été par la personne (lu une fois, puis oublié). */
export function takeHatChosen(): boolean { const x = hatChosen; hatChosen = false; return x; }

/** Une équipe que la casquette affichée ne montre pas : la casquette qui la montre (le staff d'abord).
 *  Ouvrir une séance des U13 en « Joueuse · SF1 » (depuis une notification…) passe en « Coach · U13 ». */
export function hatShowing(c: Ctx, teamId: string): HatKey | null {
  if (c.hats.length < 2 || c.teams.some(t => t.id === teamId)) return null;
  const f = c.full, self = f.people.filter(p => p.user_id === c.uid), kids = f.people.filter(p => p.user_id !== c.uid);
  const plays = (ps: Person[]) => f.memberships.some(m => m.team_id === teamId && ps.some(p => p.id === m.person_id));
  const order: [HatKey, boolean][] = [['coach', f.pureCoach.has(teamId)], ['technique', f.techOf.has(teamId)], ['joueur', plays(self)],
    ['parent', plays(kids)], ['bureau', f.adminOf.length > 0 && f.teams.some(t => t.id === teamId)]];
  return order.find(([k, ok]) => ok && c.hats.some(h => h.key === k))?.[0] ?? null;
}

/** La casquette où je suis du staff de cette équipe (coach d'abord, puis responsable technique, puis bureau), quand la
 *  casquette affichée ne l'est pas : une alerte FFBB qui m'est adressée (D-117) s'ouvre là où je peux trancher, même
 *  depuis la casquette Parent ou Joueuse de la même équipe. */
export function staffHatFor(c: Ctx, teamId: string): HatKey | null {
  if (c.hats.length < 2 || c.staffOf.has(teamId)) return null;
  const f = c.full, clubOf = f.teams.find(t => t.id === teamId)?.club_id;
  const order: [HatKey, boolean][] = [['coach', f.pureCoach.has(teamId)], ['technique', f.techOf.has(teamId)],
    ['bureau', !!clubOf && f.adminOf.some(a => a.id === clubOf)]];
  return order.find(([k, ok]) => ok && k !== c.hat && c.hats.some(h => h.key === k))?.[0] ?? null;
}

/** Les casquettes du staff, dans l'ordre où l'on cherche celle qui montre une page ou une carte du staff (D-072). */
export const STAFF_HATS: HatKey[] = ['coach', 'technique', 'bureau'];
/** La première de ces casquettes que j'ai, quand la casquette affichée n'en est pas une (sinon null : on reste). */
export function hatAmong(c: Ctx, keys: HatKey[]): HatKey | null {
  if (c.hats.length < 2 || (c.hat && keys.includes(c.hat))) return null;
  return keys.find(k => c.hats.some(h => h.key === k)) ?? null;
}

/** Recharge le contexte sur place (après création d'équipe, d'invitation…). */
export async function refreshContext(ctx: Ctx): Promise<void> {
  const fresh = await loadContext();
  if (fresh) Object.assign(ctx, fresh);
}

export const teamOf = (ctx: Ctx, id: string) => ctx.teams.find(t => t.id === id) ?? null;
/** Nom de l'équipe engagée (SF1…), sinon null. */
export const squadName = (ctx: Ctx, id: string | null | undefined) => (id ? ctx.squads.find(s => s.id === id)?.name ?? null : null);
/** Ce qu'on affiche pour l'équipe d'un match : l'équipe engagée, sinon le groupe. */
export const matchTeamLabel = (ctx: Ctx, teamId: string, squadId: string | null | undefined) => squadName(ctx, squadId) ?? teamOf(ctx, teamId)?.name ?? '';

/** Genre utilisé pour accorder les libellés d'une personne : celui de son équipe. */
export function genderOf(ctx: Ctx, personId: string): TeamGender {
  const m = ctx.memberships.find(x => x.person_id === personId);
  return (m && teamOf(ctx, m.team_id)?.gender) || 'X';
}

/** Mes équipes « de travail » : celles que j'encadre, puis celles où je (ou mon enfant) joue —
 *  une coach des U13 qui joue en SF1 retrouve aussi les plays et les matchs de SF1 (casquettes multiples). */
export function workTeams(c: Ctx): Team[] {
  const mine = new Set(c.memberships.map(m => m.team_id));
  const staff = c.teams.filter(t => c.staffOf.has(t.id));
  return [...staff, ...c.teams.filter(t => mine.has(t.id) && !c.staffOf.has(t.id))];
}

/** Mes enfants : les fiches que je représente sans être la mienne. */
export const children = (ctx: Ctx) => ctx.people.filter(p => p.user_id !== ctx.uid);
export const me = (ctx: Ctx) => ctx.people.find(p => p.user_id === ctx.uid) ?? null;

/** Prénom pour saluer : ma fiche de joueuse, sinon le prénom du compte. */
export const firstName = (ctx: Ctx) => { const self = me(ctx); return self ? displayName(self) : ctx.profileName ?? ''; };

/** Compte d'une mineure (code perso, ou moins de 18 ans) : ses parents gèrent argent, bénévolat, inscriptions. */
export function isMinorAccount(ctx: Ctx): boolean {
  const self = me(ctx);
  if (!self) return false;
  if (self.login_handle) return true;
  if (!self.birth_date) return false;
  const b = new Date(`${self.birth_date}T12:00:00`), n = new Date();
  const age = n.getFullYear() - b.getFullYear() - (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate()) ? 1 : 0);
  return age < 18;
}

/** Rôle principal, qui décide de l'accueil et des onglets (maquettes D-024). */
export type Persona = 'coach' | 'bureau' | 'joueur' | 'parent' | 'partenaire' | 'supporter' | 'abonne' | 'membre';
export function personaOf(c: Ctx): Persona {
  // Casquette Bénévole (D-097) : l'accueil et les onglets du supporter, quel que soit le rôle principal.
  if (c.hat === 'benevole') return 'supporter';
  if (c.partners.length && !c.people.length && !c.adminOf.length && !c.coachOf.size) return 'partenaire';
  if (c.coachOf.size) return 'coach';
  if (c.adminOf.length) return 'bureau';
  const self = me(c);
  if (self && c.memberships.some(m => m.person_id === self.id)) return 'joueur';
  if (children(c).length) return 'parent';
  // Supporter (D-064) : ni joueur, ni parent, ni coach ; il suit le club et donne un coup de main.
  if (c.roles.some(r => r.role === 'supporter' && (!c.club || r.club_id === c.club.id))) return 'supporter';
  // Abonné (D-110) : les matchs du club et ses suivis, rien de la vie interne.
  if (!c.roles.length && c.club && c.followerOf?.includes(c.club.id)) return 'abonne';
  return 'membre';
}
